/***
 *
 *   CLIENT GROUPS — STAFF DISPLAY RULES
 *   The labels and badges of the client group list and detail screens, kept out
 *   of React so client-group-v2-display.test.js covers them without rendering
 *   the pages.
 *
 *   v1 groups render exactly as they did before this module existed: the v1
 *   tables and helpers below were moved here unchanged, and the test's v1
 *   characterization snapshot pins the markup they produce.
 *
 *   A v2 group (metadata.form_version = client_onboarding_v2) adds, for each
 *   party row (metadata.party_id):
 *
 *     v2RoleBadges    one badge per role the party holds, with v2 labels. Never
 *                     "≥25%" and never a raw slug (R22).
 *     v2Badges        the markers: not sent to XPM, check not required, no
 *                     email, unsynced roles (R12, R20).
 *     tfnStatus       the TFN hold or write state. Never the value (KTD13).
 *     partyControls   the staff edits the approval allows now (R21).
 *
 *   and for the group: the approval panel's visibility, the stored warnings
 *   shown while it is hidden, the Archive prompt, the XPM preview's sections,
 *   and the list's Awaiting approval filter. The server rules these
 *   mirror live in helper/clientOnboardingV2.js; the server re-checks each one.
 *
 *   While the approval is pending, a staff edit re-runs the rules on the party
 *   rows but leaves the group's stored relationship lists as the submission
 *   wrote them (approve rebuilds them). So while pending the loaded preview,
 *   not the group metadata, is the source of the unsynced roles and of the
 *   fields staff may edit (liveUnsyncedRoles, editableFields).
 *
 **********/

// The shared rules are CommonJS, because the server requires them too.
import rules from "../verification/client-onboarding-v2-rules.js";

export const ENTITY_TYPE_LABELS = {
  individual: "Individual",
  trust: "Trust",
  unit_trust: "Unit Trust",
  smsf: "SMSF",
  body_corporate: "Company",
  not_for_profit: "Not for Profit",
  partnership: "Partnership",
  unincorporated_association: "Association",
  government_body: "Government Body",
  deceased_estate: "Deceased Estate",
};

export const ROLE_LABELS = {
  self: "Primary",
  trustee: "Trustee",
  settlor: "Settlor",
  appointor: "Appointor",
  protector: "Protector",
  director: "Director",
  beneficial_owner: "Beneficial Owner (≥25%)",
  partner: "Partner",
  office_holder: "Office Holder",
  executor: "Executor / Administrator",
  ceo_equivalent: "CEO / Senior Managing Official",
};

// ROLE_LABELS covers the derived-person roles; the relationships page offers two
// more that never appear as a nested individual's role.
export const RELATIONSHIP_ROLE_LABELS = {
  ...ROLE_LABELS,
  shareholder: "Shareholder",
  other: "Other",
};

// Why an edge never reached XPM, in words a reviewer can act on.
export const EDGE_SKIP_REASONS = {
  blank_endpoint: "One side of the relationship was left blank.",
  unknown_entity:
    "One side names an entity that is not in this group. People entered inside an entity become XPM contacts and cannot be relationship endpoints.",
  ambiguous_entity:
    "Two entities in this group share that display name, so the intended one could not be determined.",
  self_reference: "Both sides name the same entity.",
  duplicate_edge: "The same relationship was submitted more than once.",
  endpoint_missing_uuid:
    "One side has not been created in XPM yet. Sync it, then re-run the group sync.",
  missing_role: "No role was selected.",
  disabled_by_settings:
    "This role is set to Skip in Client Onboarding Settings.",
};

export const XPM_EDGE_STATUS_COLORS = {
  created: "bg-sg-green-bg text-sg-green",
  skipped: "bg-slate-100 text-slate-600",
  failed: "bg-sg-red-bg text-sg-red",
};

export const XPM_GROUP_STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  partial: "bg-sg-orange-bg text-sg-orange",
  complete: "bg-sg-green-bg text-sg-green",
  failed: "bg-sg-red-bg text-sg-red",
};

// A client_group_entity row's status.
export const ENTITY_STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  verification_sent: "bg-sg-blue-tint text-sg-blue-ink",
  verified: "bg-sg-green-bg text-sg-green",
  review_required: "bg-sg-orange-bg text-sg-orange",
  complete: "bg-sg-green-bg text-sg-green",
};

// A client_group row's status.
export const GROUP_STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700",
  submitted: "bg-sg-blue-tint text-sg-blue-ink",
  in_review: "bg-sg-orange-bg text-sg-orange",
  complete: "bg-sg-green-bg text-sg-green",
  archived: "bg-gray-200 text-gray-500",
};

export const parseMeta = (value) =>
  typeof value === "string" ? JSON.parse(value || "{}") : value || {};

export const entityTypeLabel = (type) => ENTITY_TYPE_LABELS[type] || type;

export const v1RoleLabel = (role) => ROLE_LABELS[role] || role;

// Coerced, not just looked up: these values originate in a public unauthenticated
// form, and rendering a non-string straight into JSX crashes the whole page.
export const relationshipRoleLabel = (role) => {
  if (typeof role !== "string" || !role) return "—";
  return RELATIONSHIP_ROLE_LABELS[role] || role;
};

export const edgeSkipReasonText = (reason) => EDGE_SKIP_REASONS[reason] || reason;

// Only the first underscore becomes a space, as the screens have always shown it.
const statusText = (status) => status?.replace("_", " ");

export const entityStatusBadge = (status) => ({
  text: statusText(status),
  color: ENTITY_STATUS_COLORS[status] || ENTITY_STATUS_COLORS.pending,
});

export const groupStatusBadge = (status) => ({
  text: statusText(status),
  color: GROUP_STATUS_COLORS[status] || GROUP_STATUS_COLORS.draft,
});

export const xpmGroupStatusColor = (status) =>
  XPM_GROUP_STATUS_COLORS[status] || "bg-gray-100 text-gray-700";

// One relationship edge's XPM sync state, as the relationships card shows it.
export const edgeSyncBadge = (sync) => ({
  text: sync.status === "created" ? `XPM: ${sync.xpm_type}` : `XPM: ${sync.status}`,
  title: sync.error || EDGE_SKIP_REASONS[sync.reason] || sync.reason || "",
  color: XPM_EDGE_STATUS_COLORS[sync.status] || "bg-gray-100 text-gray-600",
});

/*
 * v1FlagBadges()
 * The Flags column of a v1 row: null when the cell is empty, else its badges.
 * A row with verification_skipped for any other reason keeps the empty badge
 * list (and the empty wrapper) it has always rendered.
 */
export function v1FlagBadges(metadata) {
  const skipped = metadata?.verification_skipped;
  if (!metadata?.potential_duplicate && !skipped) return null;
  const badges = [];
  if (metadata?.potential_duplicate) {
    badges.push({
      key: "potential_duplicate",
      text: "Potential Duplicate",
      color: "bg-sg-orange-bg text-sg-orange",
    });
  }
  // This row kept status "pending" and a null verification_id so staff could
  // still trigger it, which means the Actions column shows it a bare Verify
  // button. Without this badge that button reads as "nobody has checked this
  // person" and clicking it creates the duplicate verification the dedup just
  // prevented.
  if (skipped?.reason === "duplicate_email") {
    badges.push({
      key: "duplicate_email",
      text: "Duplicate Email",
      color: "bg-slate-100 text-slate-700",
      title: `${skipped.email} is already being verified elsewhere in this group. Use Verify only if this role needs its own separate check.`,
    });
  }
  return badges;
}

// ---------------------------------------------------------------------------
// v2 groups
// ---------------------------------------------------------------------------

// client_group.metadata.form_version on every group the v2 submission writes
// (CLIENT_ONBOARDING_V2_FORM_VERSION in helper/clientOnboardingCore.js).
export const V2_FORM_VERSION = "client_onboarding_v2";

// R21: every party field while the approval is pending and nothing is in XPM,
// only the email after that (STAFF_EDIT_FIELDS and LATE_EDIT_FIELDS in
// helper/clientOnboardingV2.js).
export const STAFF_EDIT_FIELDS = ["is_client", "parent_entity_id", "exclude", "email"];
const LATE_EDIT_FIELDS = ["email"];

// A TFN hold with less time than this left is shown as a warning.
export const TFN_HOLD_WARNING_MS = 48 * 60 * 60 * 1000;

// tfn_sync_status values (TFN_SYNC_STATUS in helper/redactTfn.js).
const TFN_SYNC_STATUSES = ["pending", "updated", "failed"];

// tfn_sync_status values after which the job is done with the TFN: written, or
// given up (isTfnSyncTerminal in job/clientOnboardingSync.js). The row label
// (tfnStatus) and the Archive prompt (holdsLiveTfn) both read this one list.
const TFN_SYNC_TERMINAL = ["updated", "failed"];

const { isPlainObject, asArray } = rules;

const asMap = (value) => (isPlainObject(value) ? value : {});

// Own keys only: a marker reason comes from stored data, and "constructor" is
// not a label.
const lookup = (table, key) =>
  typeof key === "string" && Object.prototype.hasOwnProperty.call(table, key)
    ? table[key]
    : undefined;

const nameOr = (name) => rules.trimmedString(name) || "(no name)";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/*
 * readMeta()
 * A metadata or data column as an object. The list and the detail rows carry
 * it as a JSON string or as an object. Unlike parseMeta (kept as it was for the
 * v1 paths), a value that does not parse is {}, never a throw.
 */
export function readMeta(value) {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value || "{}");
    } catch (err) {
      return {};
    }
  }
  return asMap(parsed);
}

export const isV2Group = (group) =>
  readMeta(group && group.metadata).form_version === V2_FORM_VERSION;

// A party row of a v2 group. A UBO-report row in the same group has no
// party_id and renders as a v1 row (KTD2).
export const isV2PartyRow = (row) => Boolean(readMeta(row && row.metadata).party_id);

/*
 * xpmApprovalStatus()
 * metadata.xpm_approval.status of a v2 group: not_required, pending, approved
 * or rejected. null for any other group, as xpmApprovalOf on the server.
 */
export function xpmApprovalStatus(group) {
  const metadata = readMeta(group && group.metadata);
  if (metadata.form_version !== V2_FORM_VERSION) return null;
  const status = asMap(metadata.xpm_approval).status;
  return typeof status === "string" ? status : null;
}

export const isAwaitingApproval = (group) => xpmApprovalStatus(group) === "pending";

// The list's Awaiting approval filter (R21), in the browser: the list endpoint
// already returns each group's metadata.
export const filterAwaitingApproval = (groups) => asArray(groups).filter(isAwaitingApproval);

// ---------------------------------------------------------------------------
// v2 labels (R22)
// ---------------------------------------------------------------------------

/*
 * v2RoleLabel()
 * Mirrors v2RoleLabel in helper/clientOnboardingV2.js, which is server only:
 * the rules label, the client's own text for "other", else the slug in words.
 * Never "≥25%" (v2 has no ownership threshold for parties), never a raw slug.
 */
export function v2RoleLabel(role, roleOther) {
  const key = rules.trimmedString(role);
  const other = rules.trimmedString(roleOther);
  if (key === "other" && other) return other;
  const found = rules.ROLES.find((r) => r.value === key);
  if (found) return found.text;
  const words = key.replace(/(?:_|-)+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

// One badge for each role the party holds (metadata.roles, by row id), with the
// entity it is held at. The ownership figure is the client's own answer.
export function v2RoleBadges(metadata) {
  const badges = [];
  asArray(metadata && metadata.roles).forEach((role, i) => {
    if (!isPlainObject(role)) return;
    const label = v2RoleLabel(role.role, role.role_other) || "Role";
    const pct = Number.isFinite(role.ownership_pct) ? ` (${role.ownership_pct}%)` : "";
    badges.push({
      key: String(i),
      text: `${label}${pct}`,
      entityName: rules.trimmedString(role.entity_name),
    });
  });
  return badges;
}

// The relationships card on a v2 group uses v2 labels; a v1 group keeps its own.
export const relationshipRoleText = (v2, role, roleOther) =>
  v2 ? v2RoleLabel(role, roleOther) || "—" : relationshipRoleLabel(role);

// ---------------------------------------------------------------------------
// v2 markers (R12, R20)
// ---------------------------------------------------------------------------

// metadata.pms_exclude.reason: why a party makes no XPM record.
export const PMS_EXCLUDE_REASON_LABELS = {
  no_email: "No email",
  ceo_only: "CEO or senior managing official only",
  parent_excluded: "Parent entity excluded",
  staff_excluded: "Excluded by staff",
};
export const PMS_EXCLUDE_REASONS = {
  no_email:
    "Not onboarded as a client and has no email address, so XPM cannot get a contact for them.",
  ceo_only:
    "Named only as the CEO or senior managing official. They hold no role in an entity, so XPM gets no record for them.",
  parent_excluded:
    "Every entity they hold a role in is excluded from XPM, so there is no client for them to be a contact of.",
  staff_excluded: "Excluded from XPM by staff.",
};

// A no_email exclusion on a row that has an email now. While pending, an added
// email re-runs the rules and ends the exclusion; after XPM sync started, R21
// keeps the exclusion and the email is only for the identity check.
export const NO_EMAIL_ADDED_LATER =
  "Had no email address when XPM sync started, so XPM has no contact for them. " +
  "The email added since is used for the identity check only.";

// metadata.verification_exempt.reason, other than no_email: why a person gets
// no identity check (R15).
export const VERIFICATION_EXEMPT_REASON_LABELS = {
  role_not_required: "Role does not need a check",
  duplicate: "Entered more than once",
};
export const VERIFICATION_EXEMPT_REASONS = {
  role_not_required:
    "Every role they hold is a shareholding under 25% or a role at a government body, so no identity check is needed.",
  duplicate:
    "Entered more than once with the same name and email. One identity check is sent for both entries.",
};

export const NO_EMAIL_BADGE = "No email. Add an email, then send the check.";

// Why a role row is recorded here and not sent to XPM (the unsynced reasons of
// client-onboarding-v2-rules.js).
export const UNSYNCED_ROLE_REASONS = {
  contact_secondary_role:
    "They are an XPM contact under another client, so this role is recorded here and not sent to XPM.",
  entity_excluded: "The entity is excluded from XPM.",
  holder_excluded: "The holder is excluded from XPM.",
};

export const unsyncedReasonText = (reason) =>
  lookup(UNSYNCED_ROLE_REASONS, reason) || edgeSkipReasonText(reason);

const unsyncedRoleLine = (skip) => {
  const row = asMap(skip.row);
  const role = v2RoleLabel(skip.role || row.relationship_role, skip.role_other) || "Role";
  const entity = rules.trimmedString(row.to_entity) || "an entity";
  return `${role} at ${entity}: ${unsyncedReasonText(skip.reason)}`;
};

/*
 * v2Badges()
 * The marker badges of one v2 party row, in this order: not sent to XPM, the
 * check (no email, or not required), unsynced roles. Each is
 * { key, text, reason, title, color }. Unsynced roles are the role rows the
 * party holds in metadata.relationship_edges_skipped of the group, less the
 * duplicates (one copy of those is sent). `liveSkipped`, when it is a list,
 * replaces that stored list: liveUnsyncedRoles while the approval is pending.
 */
export function v2Badges(row, groupMetadata, liveSkipped = null) {
  const metadata = readMeta(row && row.metadata);
  const badges = [];

  if (metadata.pms_exclude) {
    const reason = asMap(metadata.pms_exclude).reason;
    const emailAddedLater =
      reason === "no_email" && Boolean(rules.trimmedString(readMeta(row.data).email));
    badges.push({
      key: "not_sent",
      text: "Not sent to XPM",
      reason: emailAddedLater
        ? "No email when XPM sync started"
        : lookup(PMS_EXCLUDE_REASON_LABELS, reason) || "Not sent",
      title: emailAddedLater
        ? NO_EMAIL_ADDED_LATER
        : lookup(PMS_EXCLUDE_REASONS, reason) || "This party makes no XPM record.",
      color: "bg-gray-100 text-gray-700",
    });
  }

  const exemptReason = asMap(metadata.verification_exempt).reason;
  if (exemptReason === "no_email") {
    badges.push({
      key: "no_email",
      text: NO_EMAIL_BADGE,
      reason: null,
      title: "No identity check can be sent without an email address.",
      color: "bg-sg-red-bg text-sg-red",
    });
  } else if (metadata.verification_exempt) {
    badges.push({
      key: "check_not_required",
      text: "Check not required",
      reason: lookup(VERIFICATION_EXEMPT_REASON_LABELS, exemptReason) || "Not required",
      title:
        lookup(VERIFICATION_EXEMPT_REASONS, exemptReason) ||
        "This person gets no identity check.",
      color: "bg-slate-100 text-slate-700",
    });
  }

  const skipped = Array.isArray(liveSkipped)
    ? liveSkipped
    : readMeta(groupMetadata).relationship_edges_skipped;
  const unsynced = asArray(skipped).filter(
    (skip) =>
      isPlainObject(skip) &&
      skip.reason !== "duplicate_edge" &&
      Boolean(row) &&
      skip.from_entity_id === row.id
  );
  if (unsynced.length) {
    badges.push({
      key: "unsynced_roles",
      text: "Unsynced roles",
      reason: `${plural(unsynced.length, "role")} not sent to XPM`,
      title: unsynced.map(unsyncedRoleLine).join("\n"),
      color: "bg-sg-orange-bg text-sg-orange",
    });
  }

  return badges;
}

/*
 * splitSkippedRoles()
 * A v2 group's relationship_edges_skipped as the relationships card lists it:
 * the unsynced roles, and apart from them the duplicates, of which one copy is
 * sent (so they are not unsynced).
 */
export function splitSkippedRoles(list) {
  const entries = asArray(list).filter(isPlainObject);
  return {
    unsynced: entries.filter((skip) => skip.reason !== "duplicate_edge"),
    duplicates: entries.filter((skip) => skip.reason === "duplicate_edge"),
  };
}

// The XPM group's member count: the top-level rows, and on a v2 group not the
// excluded parties.
export function xpmMemberCount(group, entities) {
  const v2 = isV2Group(group);
  return asArray(entities).filter((entity) => {
    const metadata = parseMeta(entity.metadata);
    return !metadata?.parent_entity_id && !(v2 && metadata?.pms_exclude);
  }).length;
}

// ---------------------------------------------------------------------------
// v2 TFN state (KTD13). Staff see the hold and the write state, never the value.
// ---------------------------------------------------------------------------

// metadata.tfn_sync_reason: why a held TFN did not reach XPM. Read only while
// tfn_sync_status is failed.
export const TFN_SYNC_REASON_LABELS = {
  tfn_vault_expired: "the hold expired",
  tfn_hold_dropped: "the hold was removed",
  tfn_hold_disabled: "TFN holds are switched off",
};

// TFN_SYNC_REASON_MESSAGES in helper/clientOnboardingV2.js, for a row whose
// tfn_sync_error is empty.
export const TFN_SYNC_REASON_MESSAGES = {
  tfn_vault_expired:
    "The tax number was no longer held when it was needed. Use the one-time secret " +
    "link from the submission email to enter it in XPM.",
  tfn_hold_disabled:
    "Tax numbers are not held for approval at the moment, so this one was not kept. " +
    "Use the one-time secret link from the submission email to enter it in XPM.",
  tfn_hold_dropped:
    "The held tax number was removed before XPM sync was approved. Use the one-time " +
    "secret link from the submission email to enter it in XPM.",
};

// metadata.tfn_hold.drop_reason (TFN_HOLD_DROP_REASON in helper/clientOnboardingV2.js).
export const TFN_DROP_REASON_LABELS = {
  tfn_written: "written to XPM",
  tfn_write_failed: "the XPM write failed",
  tfn_vault_expired: "the hold expired",
  tfn_already_terminal: "the write had already finished",
  create_failed: "the XPM client was not created",
  staff_tfn_written: "written by staff",
  group_rejected: "XPM sync rejected",
  group_archived: "client group archived",
  entity_excluded: "excluded from XPM",
  tfn_write_disabled: "the TFN write is off",
  tfn_hold_disabled: "TFN holds are switched off",
  tfn_hold_dropped: "hold removed",
};

export function formatHoldDate(value) {
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function tfnWriteState(row, metadata, syncStatus) {
  if (syncStatus === "updated") {
    return {
      kind: "written",
      text: "TFN written to XPM",
      warning: false,
      manualWrite: false,
      color: "bg-sg-green-bg text-sg-green",
    };
  }
  if (syncStatus === "pending") {
    return {
      kind: "write_pending",
      text: "TFN write pending",
      warning: false,
      manualWrite: false,
      color: "bg-sg-blue-tint text-sg-blue-ink",
    };
  }
  // failed. Without a reason it is a v1-meaning XPM failure, and the v1 retry.
  const reason = lookup(TFN_SYNC_REASON_LABELS, metadata.tfn_sync_reason)
    ? metadata.tfn_sync_reason
    : null;
  return {
    kind: "write_failed",
    text: reason ? `TFN not written: ${TFN_SYNC_REASON_LABELS[reason]}` : "TFN not written",
    title:
      rules.trimmedString(metadata.tfn_sync_error) ||
      (reason ? TFN_SYNC_REASON_MESSAGES[reason] : "The tax number was not written"),
    warning: false,
    // The manual write needs the XPM client to write onto, as in v1.
    manualWrite: Boolean(row && row.external_id),
    manualWriteLabel: reason ? "Enter tax number" : "Retry tax number",
    color: "bg-sg-red-bg text-sg-red",
  };
}

function tfnHeldState(hold, now) {
  const expires = Date.parse(hold.expires_at);
  if (!Number.isFinite(expires)) {
    return {
      kind: "held",
      text: "TFN held",
      warning: false,
      manualWrite: false,
      color: "bg-sg-blue-tint text-sg-blue-ink",
    };
  }
  const until = formatHoldDate(hold.expires_at);
  const left = expires - now;
  if (left <= 0) {
    return {
      kind: "hold_expired",
      text: `TFN hold expired on ${until}`,
      title:
        "The held tax number is gone. Use the one-time secret link from the submission email to enter it in XPM.",
      warning: true,
      manualWrite: false,
      color: "bg-sg-orange-bg text-sg-orange",
    };
  }
  const warning = left < TFN_HOLD_WARNING_MS;
  return {
    kind: "held",
    // The warning is in the words too, not only the colour.
    text: warning ? `TFN held until ${until} (less than 48 hours left)` : `TFN held until ${until}`,
    title: warning
      ? "Less than 48 hours left. Approve XPM sync before then, or the tax number must be entered in XPM by hand."
      : "The tax number is held, encrypted, until XPM sync is approved. It is never shown here.",
    warning,
    manualWrite: false,
    color: warning ? "bg-sg-orange-bg text-sg-orange" : "bg-sg-blue-tint text-sg-blue-ink",
  };
}

// A drop whose vault delete failed (drop_ok false). The encrypted number stays
// in the vault until its time limit removes it, so the label says when, and the
// title tells ops which key to delete sooner.
function tfnDropFailedState(row, hold, reason, now) {
  const expires = Date.parse(hold.expires_at);
  const until = formatHoldDate(hold.expires_at);
  const known = Number.isFinite(expires);
  const gone = known && expires <= now;
  const rowId = rules.trimmedString(row && row.id) || "this row";
  return {
    kind: "drop_failed",
    text: known
      ? `TFN removal failed (${reason}); it ${gone ? "expired" : "expires"} on ${until}`
      : `TFN removal failed (${reason})`,
    title: gone
      ? `For ops: the vault delete failed, but the hold's time limit ended on ${until}, so the vault no longer holds the tax number.`
      : `For ops: the vault delete failed, so the encrypted tax number stays in the vault until ${
          known ? until : "its time limit ends"
        }. Delete the vault key for row ${rowId} to remove it sooner. The number is never shown here.`,
    warning: !gone,
    manualWrite: false,
    color: gone ? "bg-gray-100 text-gray-700" : "bg-sg-orange-bg text-sg-orange",
  };
}

/*
 * tfnStatus()
 * The TFN label of a v2 row, or null when it has none: { kind, text, title?,
 * warning, manualWrite, manualWriteLabel?, color }.
 *
 *   a terminal write state (updated, failed)  the write state, at any approval
 *                                              status and over any hold
 *   after approval (approved, not_required)   the write state, when there is one
 *   a dropped hold                             TFN destroyed (<reason>), or
 *                                              TFN removal failed (<reason>)
 *                                              when the vault delete failed
 *   a live hold, after approval                TFN write pending: the job has
 *                                              not reached it yet
 *   a live hold                                TFN held until <date>, a warning
 *                                              under 48 hours, expired after it
 *
 * Before approval a write still pending shows only when there is no hold (a
 * TFN refused at submit). A terminal write state wins even over an undropped
 * hold (the drop record failed): the job is done with the TFN, which is also
 * how the Archive prompt (holdsLiveTfn) reads the row. Before approval it does
 * not win over a dropped hold, so "TFN removal failed" stays visible. `now` is
 * injected for the tests.
 */
export function tfnStatus(row, approvalStatus, now = Date.now()) {
  const metadata = readMeta(row && row.metadata);
  const hold = isPlainObject(metadata.tfn_hold) ? metadata.tfn_hold : null;
  const syncStatus = TFN_SYNC_STATUSES.includes(metadata.tfn_sync_status)
    ? metadata.tfn_sync_status
    : null;
  const afterApproval = approvalStatus === "approved" || approvalStatus === "not_required";
  const nowMs = now instanceof Date ? now.getTime() : Number(now);

  // Only over an undropped hold: before approval, a dropped hold keeps its own
  // label, so a removal that failed (the vault copy still there) stays on screen.
  const terminal = TFN_SYNC_TERMINAL.includes(syncStatus) && !(hold && hold.dropped_at);
  if (syncStatus && (afterApproval || !hold || terminal)) return tfnWriteState(row, metadata, syncStatus);
  if (hold && hold.dropped_at) {
    const reason = lookup(TFN_DROP_REASON_LABELS, hold.drop_reason) || "removed";
    if (hold.drop_ok === false) return tfnDropFailedState(row, hold, reason, nowMs);
    return {
      kind: "destroyed",
      text: `TFN destroyed (${reason})`,
      title: "The held tax number was deleted.",
      warning: false,
      manualWrite: false,
      color: "bg-gray-100 text-gray-700",
    };
  }
  if (!hold) return null;
  const held = tfnHeldState(hold, nowMs);
  // Approve keeps the hold and writes no tfn_sync_status; the job sets it after
  // the write. Until then the row shows the write state, not the hold copy that
  // asks staff to approve. An expired hold stays expired: there is nothing left
  // for the job to write.
  if (afterApproval && held.kind !== "hold_expired") {
    return tfnWriteState(row, metadata, "pending");
  }
  return held;
}

// ---------------------------------------------------------------------------
// v2 approval (R21)
// ---------------------------------------------------------------------------

// The approval panel, with Approve and Reject: only while the approval is
// pending. Reject and Archive both end it (rejected, archived).
export const approvalPanelVisible = (group) =>
  Boolean(group) && group.status !== "archived" && isAwaitingApproval(group);

/*
 * storedV2Warnings()
 * metadata.v2_warnings of a v2 group (a person entered twice, two entities with
 * one name), each { key, text }, for the notice the detail page shows. Empty
 * while the approval panel shows, because the panel lists the live warnings of
 * the preview, and for any other group.
 *
 * Only submit and approve write v2_warnings, so a late "Add email" leaves a
 * client_without_email warning behind. `rows` are the group's current rows: a
 * no-email warning whose party row (row id = warning.partyId) now has an email
 * is left out.
 */
export function storedV2Warnings(group, rows = []) {
  if (!isV2Group(group) || approvalPanelVisible(group)) return [];
  const withEmail = new Set(
    asArray(rows)
      .filter((row) => isPlainObject(row) && rules.trimmedString(readMeta(row.data).email))
      .map((row) => row.id)
  );
  const warnings = [];
  asArray(readMeta(group.metadata).v2_warnings).forEach((warning, i) => {
    const text = isPlainObject(warning) ? rules.trimmedString(warning.message) : "";
    const settled = text && warning.code === "client_without_email" && withEmail.has(warning.partyId);
    if (text && !settled) warnings.push({ key: String(i), text });
  });
  return warnings;
}

// A v2 party row whose TFN hold the vault still holds: no drop recorded
// (selectTfnHoldsToDrop in helper/clientOnboardingV2.js), not past a known
// expiry, and no terminal write state. A terminal write with an undropped hold
// (the drop record failed) is done: the row label shows the write state
// (tfnStatus), so the prompt does not say the number never reaches XPM. An
// expiry that does not parse still shows as held (tfnHeldState).
function holdsLiveTfn(row, now) {
  if (!isPlainObject(row) || !isV2PartyRow(row)) return false;
  const metadata = readMeta(row.metadata);
  if (TFN_SYNC_TERMINAL.includes(metadata.tfn_sync_status)) return false;
  const hold = metadata.tfn_hold;
  if (!isPlainObject(hold) || hold.dropped_at) return false;
  const expires = Date.parse(hold.expires_at);
  return !Number.isFinite(expires) || expires > now;
}

/*
 * archivePromptText()
 * The Archive prompt. On a v2 group, archiving drops every undropped TFN hold
 * whatever the approval status, and while the approval is pending it also
 * rejects XPM sync (updateGroupStatus in controller/clientOnboardingV2Controller.js),
 * so the prompt says what it will do. `rows` are the group's current rows
 * (group.entities by default). A v1 group, and a v2 group with neither, keeps
 * the v1 prompt. `now` is injected for the tests.
 */
export function archivePromptText(group, rows = group && group.entities, now = Date.now()) {
  if (!isV2Group(group)) return "Archive reason";
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const rejects = isAwaitingApproval(group);
  const holdsTfn = asArray(rows).some((row) => holdsLiveTfn(row, nowMs));
  if (rejects && holdsTfn) {
    return "Archive this client group? This rejects XPM sync and destroys the held tax numbers. Archive reason (optional)";
  }
  if (rejects) return "Archive this client group? This rejects XPM sync. Archive reason (optional)";
  if (holdsTfn) {
    return "Archive this client group? This destroys the held tax numbers, so they never reach XPM. Archive reason (optional)";
  }
  return "Archive reason";
}

/*
 * liveUnsyncedRoles()
 * While the approval panel shows, the unsynced roles of the loaded preview state
 * (GET /v2/preview, or a party edit's answer): a staff edit does not rewrite
 * metadata.relationship_edges_skipped until approve. Each entry has the stored
 * entry's shape, from_entity_id included. null when the stored list is the
 * current one (no pending approval), or no readable preview is loaded yet.
 */
export function liveUnsyncedRoles(group, previewState) {
  if (!approvalPanelVisible(group)) return null;
  const state = asMap(previewState);
  if (asArray(state.errors).length) return null;
  const list = asMap(state.preview).unsyncedRoles;
  return Array.isArray(list) ? list : null;
}

/*
 * editableFields()
 * The party fields staff may change now. The server rule (editableV2Fields),
 * plus one of the screen's own: a rejected group shows no edit control at all.
 * `serverFields` is the server's own answer when one is loaded (editable_fields
 * of the preview state). It can only narrow the local rule, so a server rule
 * this mirror lacks hides the control, and a stale answer never brings a
 * locked field back.
 */
export function editableFields(group, rows, serverFields = null) {
  const status = xpmApprovalStatus(group);
  if (status === null || status === "rejected") return [];
  const inXpm = asArray(rows).some(
    (row) => isPlainObject(row) && row.external_id && isV2PartyRow(row)
  );
  const fields =
    status === "pending" && !inXpm ? [...STAFF_EDIT_FIELDS] : [...LATE_EDIT_FIELDS];
  return Array.isArray(serverFields)
    ? fields.filter((field) => serverFields.includes(field))
    : fields;
}

// The entities a person may have as parent: those they hold a role in that are
// not excluded (the server refuses any other with 400).
function parentOptions(metadata, rows) {
  const byId = new Map(asArray(rows).filter(isPlainObject).map((row) => [row.id, row]));
  const options = [];
  for (const role of asArray(metadata.roles)) {
    if (!isPlainObject(role)) continue;
    const id = rules.trimmedString(role.entity_id);
    if (!id || options.some((option) => option.value === id)) continue;
    const entity = byId.get(id);
    if (entity && readMeta(entity.metadata).pms_exclude) continue;
    options.push({
      value: id,
      text:
        rules.trimmedString(entity && entity.name) ||
        rules.trimmedString(role.entity_name) ||
        "(no name)",
    });
  }
  return options;
}

/*
 * partyControls()
 * The edit controls of one party row, each { field, value?, label?, options? }
 * for PATCH /api/client-groups/:id/v2/parties/:entityId:
 *
 *   is_client         a person's Yes/No answer; value is the current answer
 *   parent_entity_id  a contact person with more than one possible parent
 *   exclude           value is what the click sends: exclude, or undo a staff
 *                     exclusion. An exclusion the rules made has no toggle.
 *   email             a person with no email: "Add email"
 *
 * `serverFields` as editableFields.
 */
export function partyControls(group, row, rows, serverFields = null) {
  const metadata = readMeta(row && row.metadata);
  if (!metadata.party_id) return [];
  const fields = editableFields(group, rows, serverFields);
  const person = row.type === "individual";
  const excluded = Boolean(metadata.pms_exclude);
  const controls = [];

  if (person && fields.includes("is_client")) {
    controls.push({ field: "is_client", value: metadata.is_client === true });
  }
  if (person && fields.includes("parent_entity_id") && metadata.is_client !== true && !excluded) {
    const options = parentOptions(metadata, rows);
    if (options.length > 1) {
      controls.push({
        field: "parent_entity_id",
        value: rules.trimmedString(metadata.parent_entity_id),
        options,
      });
    }
  }
  if (fields.includes("exclude")) {
    if (!excluded) {
      controls.push({ field: "exclude", value: true, label: "Exclude from XPM" });
    } else if (asMap(metadata.pms_exclude).reason === "staff_excluded") {
      controls.push({ field: "exclude", value: false, label: "Include in XPM" });
    }
  }
  if (person && fields.includes("email") && !rules.trimmedString(readMeta(row.data).email)) {
    controls.push({ field: "email", label: "Add email" });
  }
  return controls;
}

/*
 * previewSections()
 * GET /v2/preview's `preview` (buildXpmPreview in helper/clientOnboardingV2.js)
 * as the lines the approval panel shows, each { key, text, detail? }.
 * `errors` is the state's own list: rows the rules cannot read.
 */
export function previewSections(preview, errors = []) {
  const source = asMap(preview);
  const sync = asMap(source.sync);
  const clientGroup = asMap(source.clientGroup);
  const groupName = rules.trimmedString(clientGroup.name);
  const items = (list, toItem) => asArray(list).filter(isPlainObject).map(toItem);

  return {
    syncEnabled: sync.enabled === true,
    groupSync: sync.group === true,
    relationshipSync: sync.relationships === true,
    group: groupName
      ? `${groupName} (${plural(asArray(clientGroup.members).length, "member")})`
      : null,
    clients: items(source.clients, (client, i) => ({
      key: client.entityId || String(i),
      text: nameOr(client.name),
      detail: entityTypeLabel(client.type) || "",
    })),
    contacts: items(source.contacts, (contact, i) => {
      const position =
        rules.trimmedString(contact.position) || v2RoleLabel(contact.role) || "Contact";
      const parent = rules.trimmedString(contact.parentEntityName) || "(no name)";
      const email = rules.trimmedString(contact.email) || "No email";
      return {
        key: contact.entityId || String(i),
        text: nameOr(contact.name),
        detail: `${position} at ${parent} · ${email}`,
      };
    }),
    relationships: items(source.relationships, (relationship, i) => ({
      key: relationship.edgeKey || String(i),
      text: rules.trimmedString(relationship.sentence),
    })),
    relationshipsSkipped: items(source.relationshipsSkipped, (skip, i) => ({
      key: skip.edgeKey || String(i),
      text: v2RoleLabel(skip.role) || "Role",
      detail: edgeSkipReasonText(skip.reason) || "",
    })),
    excluded: items(source.excluded, (party, i) => ({
      key: party.entityId || String(i),
      text: nameOr(party.name),
      detail: lookup(PMS_EXCLUDE_REASON_LABELS, party.reason) || "Not sent",
    })),
    unsyncedRoles: items(source.unsyncedRoles, (skip, i) => {
      const row = asMap(skip.row);
      const from = rules.trimmedString(row.from_entity) || "—";
      const to = rules.trimmedString(row.to_entity) || "—";
      const role = v2RoleLabel(skip.role || row.relationship_role, skip.role_other) || "Role";
      return {
        key: String(Number.isInteger(skip.index) ? skip.index : i),
        text: `${from} — ${role} — ${to}`,
        detail: unsyncedReasonText(skip.reason) || "",
      };
    }),
    warnings: items(source.warnings, (warning, i) => ({
      key: String(i),
      text: rules.trimmedString(warning.message),
    })),
    errors: items(errors, (error, i) => ({
      key: String(i),
      text: rules.trimmedString(error.message),
    })),
  };
}
